import { Stack, Typography, Divider } from '@mui/material';
import type { Account } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import Image from 'next/image';
import { useMemo } from 'react';

import { BackupVaultTagSelection } from '@/components/vaults/backupVaultTagSelection';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useDAL } from '@/contexts/useDAL';

import { IntegrationResourcesSelectorWrapper } from './integrationResourcesSelectorWrapper';
import type { IntegrationWizardState } from './integrationWizardFlow';
import { AmazonEmrConfiguration } from './vendorConfigurations/amazonEmrConfiguration';
import { AmazonRedshiftConfiguration } from './vendorConfigurations/amazonRedshiftConfiguration';
import { BigQueryConfiguration } from './vendorConfigurations/bigQueryConfiguration';
import { DatabricksConfiguration } from './vendorConfigurations/databricksConfiguration';
import { MicrosoftFabricConfiguration } from './vendorConfigurations/microsoftFabricConfiguration';
import { SnowflakeConfiguration } from './vendorConfigurations/snowflakeConfiguration';

import { IntegrationVendorDetails, IntegrationVendor } from '../types';

export const IntegrationConfiguration = (
  props: StepProps<IntegrationWizardState>
) => {
  const dal = useDAL();
  const { data: restoreAccountsResponse } = dal.cloudAccounts.restore.useList();
  const { data: vaultAccounts } = dal.vaults.useAccounts();

  // Get selected vault
  const vaultId = props.wizardState.vaultId;

  // Get initial resource IDs from existing config
  const initialResourcesIds = useMemo(() => {
    return (
      (props.wizardState.config?.resourceSharing as { include?: string[] })
        ?.include ?? []
    );
  }, [props.wizardState.config?.resourceSharing]);

  // Get selected resource IDs
  const selectedResourcesIds =
    (props.wizardState.config?.resourceSharing as { include?: string[] })
      ?.include ?? [];

  const validateParams = () => {
    if (props.wizardState.vendor === IntegrationVendor.AMAZON_EMR) {
      return !!props.wizardState.config?.emr?.accountId;
    }

    if (props.wizardState.vendor === IntegrationVendor.AMAZON_REDSHIFT) {
      return (
        !!props.wizardState.config?.redshift?.clusterIdentifier &&
        !!props.wizardState.config?.redshift?.database
      );
    }

    if (props.wizardState.vendor === IntegrationVendor.BIGQUERY) {
      return (
        !!props.wizardState.config?.bigquery?.dataset &&
        !!props.wizardState.config?.bigquery?.projectId
      );
    }

    if (props.wizardState.vendor === IntegrationVendor.DATABRICKS) {
      return (
        !!props.wizardState.config?.databricks?.serverHostname &&
        !!props.wizardState.config?.databricks?.oauth2ClientId
      );
    }

    if (props.wizardState.vendor === IntegrationVendor.MICROSOFT_FABRIC) {
      return (
        !!props.wizardState.config?.fabric?.connectionId &&
        !!props.wizardState.config?.fabric?.appId &&
        !!props.wizardState.config?.fabric?.subscriptionId &&
        !!props.wizardState.config?.fabric?.tenantId
      );
    }

    if (props.wizardState.vendor === IntegrationVendor.SNOWFLAKE) {
      return (
        !!props.wizardState.config?.snowflake?.accountName &&
        !!props.wizardState.config?.snowflake?.warehouse &&
        !!props.wizardState.config?.snowflake?.oauth2ClientId
      );
    }

    return false;
  };

  return (
    <StepContainer
      sx={{ padding: '40px min(80px,4vw)' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={0}
      canGoNext={validateParams}
      nextButtonText='Save Changes'
      onNextClick={() => props.finish()}
      hideStepper
      navigationComponent={
        <RenameEntityName
          label='Integration name'
          name={props.wizardState.name || ''}
          onSubmit={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
      toolbarComponent={
        <Typography className='ml-[40px]' variant='body1' maxWidth='582px'>
          Configure snapshot sharing for selected resources and define their
          target destinations.
        </Typography>
      }
    >
      <Stack
        direction='row'
        divider={<Divider orientation='vertical' flexItem />}
        gap='min(80px,4vw)'
        flexGrow={1}
      >
        <Stack width='308px' gap='24px'>
          <Typography variant='h3'>Select resources to share</Typography>
          <Stack gap='12px'>
            <Stack direction='row' alignItems='center' gap='8px'>
              <Typography noWrap variant='body1'>
                Choose a vault:
              </Typography>
              <BackupVaultTagSelection
                entityName='resources'
                vaultsPredicate={(vault) => {
                  // For Redshift, only show AWS vaults
                  if (
                    props.wizardState.vendor ===
                    IntegrationVendor.AMAZON_REDSHIFT
                  ) {
                    const account = vaultAccounts?.accounts.find(
                      (a: Account) => a.id === vault.vaultAccountId
                    );
                    return account?.cloudProvider === Provider.Aws;
                  }
                  // For other vendors, show all vaults
                  return true;
                }}
                vaultId={vaultId}
                onUpdate={(vault) => {
                  const previousVaultId = props.wizardState.vaultId;
                  props.setWizardState((state) => {
                    // Clear selected resources if vault changed
                    const shouldClearResources =
                      previousVaultId && previousVaultId !== vault.id;
                    return {
                      ...state,
                      vaultId: vault.id,

                      config: {
                        ...state.config,
                        resourceSharing: shouldClearResources
                          ? { resourceIds: [] }
                          : state.config?.resourceSharing,
                      },
                    };
                  });
                }}
              />
            </Stack>
          </Stack>
          <Typography
            variant='body1'
            sx={{
              color: !vaultId ? 'var(--mui-palette-text-disabled)' : undefined,
            }}
          >
            {`Select database resources backed up to the selected vault to share.`}
          </Typography>
          <IntegrationResourcesSelectorWrapper
            disabled={!vaultId}
            vaultId={vaultId}
            initialResourcesIds={initialResourcesIds}
            selectedResourcesIds={selectedResourcesIds}
            addResource={(providerResourceId) =>
              props.setWizardState((state) => {
                const currentInclude =
                  (state.config?.resourceSharing as { include?: string[] })
                    ?.include ?? [];
                return {
                  ...state,
                  config: {
                    ...state.config,
                    resourceSharing: {
                      ...state.config?.resourceSharing,
                      include: [...currentInclude, providerResourceId],
                    },
                  },
                };
              })
            }
            removeResource={(providerResourceId) =>
              props.setWizardState((state) => {
                const currentInclude =
                  (state.config?.resourceSharing as { include?: string[] })
                    ?.include ?? [];
                return {
                  ...state,
                  config: {
                    ...state.config,
                    resourceSharing: {
                      ...state.config?.resourceSharing,
                      include: currentInclude.filter(
                        (r: string) => r !== providerResourceId
                      ),
                    },
                  },
                };
              })
            }
          />
        </Stack>
        <Stack width='65%' gap='24px'>
          <Stack direction='row' alignItems='center' gap='12px'>
            {props.wizardState.vendor &&
              IntegrationVendorDetails[props.wizardState.vendor] && (
                <Image
                  alt={
                    IntegrationVendorDetails[props.wizardState.vendor]
                      ?.displayName
                  }
                  src={IntegrationVendorDetails[props.wizardState.vendor].logo}
                  width={32}
                  height={32}
                  style={{ width: '32px', height: '32px' }}
                />
              )}
            <Typography variant='h3'>
              Configure{' '}
              {IntegrationVendorDetails[props.wizardState.vendor]?.displayName}{' '}
              connection
            </Typography>
          </Stack>
          {props.wizardState.vendor === IntegrationVendor.AMAZON_EMR && (
            <AmazonEmrConfiguration
              wizardState={props.wizardState}
              setWizardState={props.setWizardState}
            />
          )}
          {props.wizardState.vendor === IntegrationVendor.AMAZON_REDSHIFT && (
            <AmazonRedshiftConfiguration
              wizardState={props.wizardState}
              setWizardState={props.setWizardState}
              restoreAccountsResponse={restoreAccountsResponse}
            />
          )}
          {props.wizardState.vendor === IntegrationVendor.BIGQUERY && (
            <BigQueryConfiguration
              wizardState={props.wizardState}
              setWizardState={props.setWizardState}
              restoreAccountsResponse={restoreAccountsResponse}
            />
          )}
          {props.wizardState.vendor === IntegrationVendor.DATABRICKS && (
            <DatabricksConfiguration
              wizardState={props.wizardState}
              setWizardState={props.setWizardState}
            />
          )}
          {props.wizardState.vendor === IntegrationVendor.MICROSOFT_FABRIC && (
            <MicrosoftFabricConfiguration
              wizardState={props.wizardState}
              setWizardState={props.setWizardState}
            />
          )}
          {props.wizardState.vendor === IntegrationVendor.SNOWFLAKE && (
            <SnowflakeConfiguration
              wizardState={props.wizardState}
              setWizardState={props.setWizardState}
            />
          )}
        </Stack>
      </Stack>
    </StepContainer>
  );
};
